import React from "react";
import {
  Box, Container, Heading, Text, VStack, SimpleGrid,
  HStack, Button, Icon, Table, Thead, Tbody, Tr, Th, Td, Divider
} from "@chakra-ui/react";
import { FaFlask, FaTint, FaArrowRight, FaCheckCircle } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const WATER_BLUE = "#6FAFD6";
const DEEP_AQUA = "#2C7DA0";
const ICE_MIST = "#EAF3FB";

const WaterBlogROvsWholeHome: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/water/blog/reverse-osmosis-vs-whole-home"
        fallbackTitle="RO vs Whole-Home Filtration: Which Is Right for Your Home? — One Water Australia"
        fallbackDescription="Should you get a reverse osmosis system, a whole-home filtration system, or both? Honest comparison — these aren't competing technologies."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box position="relative" bg={NAVY} color="white" py={{ base: 14, md: 20 }} overflow="hidden">
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgImage="url('https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1920&q=80')" bgSize="cover" bgPosition="center" zIndex={0} />
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgGradient={`linear(to-br, ${NAVY}E8, ${DEEP_AQUA}AA)`} zIndex={0} />
        <Container maxW="container.md" position="relative" zIndex={1}>
          <VStack spacing={5} textAlign="center">
            <Text fontSize="sm" fontWeight="700" color={WATER_BLUE} textTransform="uppercase" letterSpacing="wider">
              Comparison Guide
            </Text>
            <Heading size="2xl" fontWeight="bold" letterSpacing="-0.02em">
              RO vs Whole-Home Filtration: Which Is Right for Your Home?
            </Heading>
            <Text fontSize="lg" color="whiteAlpha.800" maxW="2xl">
              Not competing technologies. Complementary layers.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Featured image */}
      <Box h={{ base: "240px", md: "420px" }} overflow="hidden">
        <Box
          as="img"
          src="https://images.unsplash.com/photo-1584467541268-b040f83be3fd?auto=format&fit=crop&w=1200&q=80"
          alt="Modern kitchen water filtration — RO vs whole-home comparison"
          w="full"
          h="full"
          objectFit="cover"
          objectPosition="center"
        />
      </Box>

      <Container maxW="container.md" py={16}>
        <VStack spacing={12} align="stretch">

          {/* The question everyone asks */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>The Question Everyone Asks</Heading>
            <Box p={6} bg={ICE_MIST} borderRadius="xl" border={`1px solid ${WATER_BLUE}30`} mb={6}>
              <Text color="gray.700" fontSize="lg" lineHeight="tall" fontStyle="italic">
                "If you've spent time researching water filtration, you've hit this question and found contradictory answers."
              </Text>
            </Box>
            <Text color="gray.700" mb={4} lineHeight="tall">
              The confusion comes from the fact that reverse osmosis and whole-home filtration are often presented as competitors — as if you have to pick one. They're not. They solve different problems, at different points in the water's journey through your home.
            </Text>
            <Text color="gray.700" lineHeight="tall">
              Understanding what each system actually does — independently — is the prerequisite for understanding when you need one, the other, or both.
            </Text>
          </Box>

          <Divider borderColor="gray.200" />

          {/* What each does */}
          <Box>
            <Heading size="lg" color={NAVY} mb={6}>What Each System Actually Does</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Box p={7} bg={ICE_MIST} borderRadius="xl" border={`2px solid ${WATER_BLUE}40`}>
                <Icon as={FaFlask} boxSize={8} color={DEEP_AQUA} mb={4} />
                <Heading size="md" color={NAVY} mb={4}>Reverse Osmosis (RO)</Heading>
                <VStack align="flex-start" spacing={3} mb={4}>
                  {[
                    { label: "Location", val: "Under your kitchen sink (point-of-use)" },
                    { label: "Scope", val: "One tap — drinking and cooking water" },
                    { label: "Membrane", val: "0.0001 micron — removes dissolved solids" },
                    { label: "Removes", val: "Dissolved solids, heavy metals, fluoride, nitrates, sodium, many pharmaceuticals" },
                    { label: "Doesn't remove", val: "Chlorine without pre-filter, sediment without pre-filter" },
                    { label: "Wastewater", val: "Produces 2–4L waste per 1L clean water" },
                  ].map((item) => (
                    <Box key={item.label}>
                      <Text fontSize="xs" fontWeight="700" color={DEEP_AQUA} textTransform="uppercase">{item.label}</Text>
                      <Text fontSize="sm" color="gray.700">{item.val}</Text>
                    </Box>
                  ))}
                </VStack>
                <Box p={3} bg="white" borderRadius="lg">
                  <Text fontSize="sm" fontWeight="600" color={NAVY}>
                    "RO = depth of filtration at one tap."
                  </Text>
                </Box>
              </Box>
              <Box p={7} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200">
                <Icon as={FaTint} boxSize={8} color={WATER_BLUE} mb={4} />
                <Heading size="md" color={NAVY} mb={4}>Whole-Home Filtration</Heading>
                <VStack align="flex-start" spacing={3} mb={4}>
                  {[
                    { label: "Location", val: "Main water entry (point-of-entry)" },
                    { label: "Scope", val: "Every tap, shower, appliance in the property" },
                    { label: "Technology", val: "Sediment + activated carbon (± UV)" },
                    { label: "Removes", val: "Chlorine, chloramines, sediment, taste, odour, some VOCs" },
                    { label: "Doesn't remove", val: "Dissolved solids, fluoride, heavy metals, nitrates" },
                    { label: "Wastewater", val: "No wastewater production" },
                  ].map((item) => (
                    <Box key={item.label}>
                      <Text fontSize="xs" fontWeight="700" color={WATER_BLUE} textTransform="uppercase">{item.label}</Text>
                      <Text fontSize="sm" color="gray.700">{item.val}</Text>
                    </Box>
                  ))}
                </VStack>
                <Box p={3} bg={ICE_MIST} borderRadius="lg">
                  <Text fontSize="sm" fontWeight="600" color={NAVY}>
                    "Whole-home = breadth of filtration across the entire house."
                  </Text>
                </Box>
              </Box>
            </SimpleGrid>
          </Box>

          <Divider borderColor="gray.200" />

          {/* Best for */}
          <Box>
            <Heading size="lg" color={NAVY} mb={6}>What Each System Is Best For</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Box>
                <Text fontWeight="700" color={NAVY} mb={4} fontSize="sm" textTransform="uppercase" letterSpacing="wider">RO is best for:</Text>
                <VStack align="flex-start" spacing={2}>
                  {[
                    "Highest-purity drinking water",
                    "Fluoride removal from drinking water",
                    "Heavy metal or nitrate concerns",
                    "High TDS or bore water supplies",
                    "Specific health-driven purity requirements",
                  ].map((item) => (
                    <HStack key={item} spacing={2}>
                      <Icon as={FaCheckCircle} color={DEEP_AQUA} boxSize={4} flexShrink={0} />
                      <Text fontSize="sm" color="gray.700">{item}</Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
              <Box>
                <Text fontWeight="700" color={NAVY} mb={4} fontSize="sm" textTransform="uppercase" letterSpacing="wider">Whole-home is best for:</Text>
                <VStack align="flex-start" spacing={2}>
                  {[
                    "Chlorine removal at every tap and shower",
                    "Skin and hair sensitivity to chlorine",
                    "Protecting appliances from sediment and chlorine",
                    "Eliminating chlorine taste and odour throughout the home",
                    "Whole-property water quality improvement",
                  ].map((item) => (
                    <HStack key={item} spacing={2}>
                      <Icon as={FaCheckCircle} color={WATER_BLUE} boxSize={4} flexShrink={0} />
                      <Text fontSize="sm" color="gray.700">{item}</Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            </SimpleGrid>
          </Box>

          <Divider borderColor="gray.200" />

          {/* Trade-offs */}
          <Box>
            <Heading size="lg" color={NAVY} mb={6}>The Honest Trade-Offs</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Box p={6} bg={ICE_MIST} borderRadius="xl" border="1px solid" borderColor={`${WATER_BLUE}40`}>
                <Heading size="sm" color={NAVY} mb={4}>RO trade-offs</Heading>
                <VStack align="flex-start" spacing={2}>
                  {[
                    "Treats one tap only — doesn't address whole-home chlorine",
                    "Produces 2–4L wastewater per 1L clean water",
                    "Removes beneficial minerals (remineralisation stage available)",
                    "Slower water flow — typically supplemented by storage tank",
                    "Requires pre-filtration — chlorine damages the membrane",
                    "Regular membrane replacement needed",
                  ].map((item) => (
                    <HStack key={item} spacing={2} align="flex-start">
                      <Text color={DEEP_AQUA} fontSize="sm" flexShrink={0} mt={0.5}>—</Text>
                      <Text fontSize="sm" color="gray.600">{item}</Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
              <Box p={6} bg={ICE_MIST} borderRadius="xl" border="1px solid" borderColor={`${WATER_BLUE}40`}>
                <Heading size="sm" color={NAVY} mb={4}>Whole-home trade-offs</Heading>
                <VStack align="flex-start" spacing={2}>
                  {[
                    "Doesn't remove dissolved solids, fluoride, or heavy metals",
                    "Carbon filter media requires periodic replacement",
                    "No residual disinfectant in pipes — important to consider",
                    "Higher upfront installation cost vs point-of-use",
                  ].map((item) => (
                    <HStack key={item} spacing={2} align="flex-start">
                      <Text color={DEEP_AQUA} fontSize="sm" flexShrink={0} mt={0.5}>—</Text>
                      <Text fontSize="sm" color="gray.600">{item}</Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            </SimpleGrid>
          </Box>

          <Divider borderColor="gray.200" />

          {/* The answer people don't expect */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>The Answer Most People Don't Expect: You Might Want Both</Heading>
            <Box p={6} bg={ICE_MIST} borderRadius="xl" border={`2px solid ${WATER_BLUE}40`} mb={6}>
              <Text color="gray.700" fontSize="lg" lineHeight="tall">
                A whole-home carbon filter upstream of your RO system actually{" "}
                <Text as="span" fontWeight="700" color={NAVY}>protects the RO membrane from chlorine damage.</Text>{" "}
                The systems don't just work together conceptually — they work together technically.
              </Text>
            </Box>
            <Text color="gray.700" mb={4} lineHeight="tall">
              RO membranes degrade when exposed to chlorine. Australian mains water contains chlorine. This means every under-sink RO system installed on mains water requires a carbon pre-filter as part of the system.
            </Text>
            <Text color="gray.700" mb={4} lineHeight="tall">
              When you add a whole-home carbon filter upstream of an RO system, the whole-home filter does double duty: it addresses whole-home chlorine AND protects the RO membrane. The RO system then provides point-of-use depth of filtration — removing dissolved solids and minerals that the whole-home filter doesn't touch.
            </Text>
            <Text color="gray.700" lineHeight="tall">
              This is the combination most households with comprehensive water quality concerns end up with — and it's the combination we often recommend when the assessment supports it.
            </Text>
          </Box>

          <Divider borderColor="gray.200" />

          {/* Decision framework */}
          <Box>
            <Heading size="lg" color={NAVY} mb={6}>How to Decide: A Simple Framework</Heading>
            <VStack spacing={4} align="stretch">
              {[
                { condition: "Chlorine, dry skin, appliance protection", recommendation: "Whole-home carbon filtration", detail: "Treats every tap and shower at the property entry point." },
                { condition: "Drinking water purity (TDS, fluoride, dissolved metals)", recommendation: "Under-sink reverse osmosis", detail: "Point-of-use depth for drinking and cooking water." },
                { condition: "Both concerns above", recommendation: "Whole-home + RO combination", detail: "Complementary layers. Recommended for most households with comprehensive concerns." },
                { condition: "Tank, bore, or rainwater", recommendation: "Multi-stage with UV", detail: "Biological protection is the priority. UV + carbon + RO as warranted." },
                { condition: "Not sure", recommendation: "Free water assessment", detail: "We test your supply and recommend the right combination — or nothing at all." },
              ].map((item) => (
                <Box key={item.condition} p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    <Box>
                      <Text fontSize="xs" fontWeight="700" color={WATER_BLUE} textTransform="uppercase" mb={1}>If you need</Text>
                      <Text fontSize="sm" color="gray.700">{item.condition}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" fontWeight="700" color={DEEP_AQUA} textTransform="uppercase" mb={1}>Recommendation</Text>
                      <Text fontSize="sm" fontWeight="600" color={NAVY}>{item.recommendation}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}>Why</Text>
                      <Text fontSize="sm" color="gray.600">{item.detail}</Text>
                    </Box>
                  </SimpleGrid>
                </Box>
              ))}
            </VStack>
          </Box>

          <Divider borderColor="gray.200" />

          {/* Australian context */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>The Australian Context</Heading>
            <Text color="gray.700" mb={4} lineHeight="tall">
              Australian mains water is chlorinated — meaning every RO installation on mains supply needs chlorine pre-treatment. This is one reason professional specification matters.
            </Text>
            <Text color="gray.700" mb={4} lineHeight="tall">
              Hard water variation across Australia (significant in parts of Gold Coast, Perth, Adelaide, and inland areas) often makes the whole-home + RO combination particularly appropriate — whole-home addresses the chlorine and scale effects throughout the property, while RO handles the elevated TDS in drinking water.
            </Text>
            <Text color="gray.700" lineHeight="tall">
              PFAS contamination near defence and industrial sites, the "last mile" pipe variability problem, and ageing infrastructure in older suburbs are all factors that a property-specific water assessment should account for.
            </Text>
          </Box>

          {/* CTA */}
          <Box bg={NAVY} p={10} borderRadius="2xl" textAlign="center">
            <VStack spacing={5}>
              <Heading size="lg" color="white">The right system starts with understanding your water.</Heading>
              <Text color="whiteAlpha.800" maxW="lg">
                Book a free assessment — whether that's whole-home, RO, both, or nothing at all.
              </Text>
              <Text fontSize="sm" color={WATER_BLUE} fontStyle="italic">
                "We're the only company honest enough to say 'you might want both, or neither.'"
              </Text>
              <Button
                as={RouterLink}
                to="/contact"
                bg={WATER_BLUE}
                color={NAVY}
                size="lg"
                fontWeight="bold"
                _hover={{ bg: "#8DC5E0" }}
                transition="all 0.2s"
              >
                Book a Free Water Assessment
              </Button>
            </VStack>
          </Box>

          {/* Related */}
          <Box>
            <Heading size="sm" color={NAVY} mb={4}>Related Reading</Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
              {[
                { title: "Reverse Osmosis Australia: Complete Guide", href: "/water/blog/reverse-osmosis-guide" },
                { title: "Water Filtration Systems", href: "/water/water-filtration" },
                { title: "Under-Sink Water Purifier", href: "/water/water-purifiers/under-sink-system" },
                { title: "Water Filters Hub", href: "/water/water-filters" },
                { title: "Is Tap Water Safe?", href: "/water/blog/tap-water-safe-australia" },
              ].map((l) => (
                <Box
                  key={l.title}
                  as={RouterLink}
                  to={l.href}
                  p={3}
                  bg="gray.50"
                  borderRadius="lg"
                  display="block"
                  textDecoration="none"
                  _hover={{ bg: ICE_MIST }}
                  transition="all 0.2s"
                >
                  <Text fontSize="sm" color={NAVY} fontWeight="500">{l.title}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default WaterBlogROvsWholeHome;
